import { Tracking } from '@terminal/services/tracking';
import { httpCode } from '@terminal/utils/api-fetch/httpCode';
import { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { WretchError } from 'wretch/resolver';

import {
  ErrorMessageWrapper,
  ErrorMessageWrapperProps,
} from '../error-message-wrapper/ErrorMessageWrapper';

export interface ErrorBoundaryWrapperProps {
  children: ReactNode;
  fallback?: ReactNode;
  resetKeys?: unknown[];
  onErrorCallback?: (error: Error) => void;
}

const isWretchError = (error: unknown): error is WretchError => {
  if (!(error instanceof Error)) {
    return false;
  }

  if (!('status' in error && 'response' in error && 'url' in error)) {
    return false;
  }

  // After narrowing with 'in', TypeScript knows 'status' exists on the error object
  return typeof error.status === 'number';
};

export const ErrorBoundaryWrapper = ({
  children,
  fallback,
  resetKeys,
  onErrorCallback,
  ...rest
}: ErrorBoundaryWrapperProps & ErrorMessageWrapperProps) => {
  const logError = (error: unknown) => {
    // Don't log 404 errors
    if (isWretchError(error) && error.status === httpCode.notFound) {
      return;
    }

    if (error instanceof Error) {
      Tracking.captureException(error);
    }
  };

  const handleError = (error: unknown) => {
    if (onErrorCallback) {
      if (error instanceof Error) {
        onErrorCallback(error);
      }
      return;
    }
    logError(error);
  };

  return (
    <ErrorBoundary
      data-testid="error-boundary-wrapper"
      fallback={
        fallback !== undefined ? (
          fallback
        ) : (
          <ErrorMessageWrapper {...rest} displayIcon />
        )
      }
      onError={handleError}
      resetKeys={resetKeys}
    >
      {children}
    </ErrorBoundary>
  );
};
